// The number, the chart and the goal, in the landing page's look and filling
// their tiles: the number as big as its tile allows, its change under it and
// its history along the foot or beside it; the chart's number over a plot
// that takes the rest; the goal as a glowing ring or a bar. Each takes its
// layout and type size from its tile (plans.ts) and sizes everything in em.
// They were in data.tsx, which the fix session holds until its push; the
// ones there go once it lands.

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowDownRight, ArrowUpRight, Minus } from "lucide-react";
import { formatNumber, timeAgo } from "../../shared/format";
import type { Point, Stream } from "../../shared/types";
import { cx } from "../lib/util";
import { TimeChart, useSize } from "../charts/charts";
import type { WP } from "./data";
import { hasHistory, inPeriod, periodChange, periodName, periodSpan, wholeDays, type Change } from "../../shared/period";
import { goalView } from "../../shared/goal";
import { usePeriod, type PeriodState } from "./DataControl";
import { set, useStore } from "../state/store";
import { quarter } from "./layout";
import { chartType, goalFit, METRIC, metricFit, metricHeadW, type MetricWidths } from "./plans";
import { useTick } from "./tick";
import { dayStats, deltaText } from "./statsData";
import { Live, nowWords, Performance, perfOf } from "./google";
import { beforeOf } from "./googleData";

type Delta = Change;

/**
 * The change, as on the landing page: up or down in the color that says whether that is good, and since when. Any
 * reading can show it (periodChange in shared/period.ts gives it). A ranking (rank, drawn upside down) goes up as its
 * number falls, by places.
 */
export function Change({ d, goodWhen, format, rank = false, short = false }: { d: Delta; goodWhen: string; format: Stream["format"]; rank?: boolean; /** the figure alone, its words in the tooltip */ short?: boolean }) {
  const text = deltaText(d, format, rank);
  const up = d.abs > 0;
  // a change too small to show is no change: never an arrow over "0.00"
  const flat = d.abs === 0 || !/[1-9]/.test(text);
  const good = flat ? null : goodWhen === "down" ? !up : up;
  const Icon = flat ? Minus : up !== rank ? ArrowUpRight : ArrowDownRight;
  return (
    <span className={cx("st-chip", good === true && "is-good", good === false && "is-bad")} title={short ? `${text} ${d.label}` : undefined}>
      <Icon strokeWidth={2.4} />
      {text}
      {!short && <span>{d.label}</span>}
    </span>
  );
}

/**
 * A widget showing a period its source has not read yet says so: reading it now, or which period it still shows
 * because the new one could not be read. Its last good data stays, dimmed while it reads.
 */
function useBehind(s: Stream, per: PeriodState): { text: string; reading: boolean } | null {
  const pull = useStore((st) => (s.pullId ? (st.pulls[s.pullId] ?? null) : null));
  const want = per.pending ?? (per.source ? per.source.days : null);
  if (want === null || !s.period || s.period.days === want) return null;
  if (per.pending !== null || pull?.status !== "error") return { text: `Reading ${periodName(want).toLowerCase()}…`, reading: true };
  return { text: `Showing ${periodName(s.period.days).toLowerCase()}. ${periodName(want)} could not be read.`, reading: false };
}

/** What a reading says beside its number when there is no change to show: its period, or its source's own words. */
function aside(s: Stream, days: number, sourced: boolean): string | null {
  if (s.period || sourced) return periodSpan(s.period?.days ?? days).replace(/^t/, "T");
  return s.label || null;
}

/** A ranking, lower being better (a search position): its line is drawn upside down and its change counted in places. */
const isRank = (item: WP["item"], s: Stream) => item.props.invert === true || (item.props.invert === undefined && (s.json as { metric?: unknown } | null)?.metric === "position");

/** The px the peak and the average take, measured at em width emW: set at k em of the widget's type, never under 10.5px. */
const statsWidth = (emW: number, px: number, k: number) => emW * px * Math.max(1, 10.5 / (k * px));

/** The peak and the average a day: two quiet lines at the head's far end, or one line under it. */
function DayStats({ st, under = false }: { st: { peak: string; when: string; avg: string }; under?: boolean }) {
  return (
    <span className={cx("st-days", under && "is-under")}>
      <span>
        Peak <b>{st.peak}</b> · {st.when}
      </span>
      <span>
        Average <b>{st.avg}</b> a day
      </span>
    </span>
  );
}

/**
 * One number, as a widget of its own: the figure as big as its tile allows, its change for the period beside it or
 * under it, and its days drawn under them as a chart that reads out the day under the pointer, with its first and
 * last dates once it is tall enough to say them; in a short wide tile the chart sits beside them.
 */
export function Metric({ item, stream }: WP) {
  const s = stream!;
  const [ref, size] = useSize<HTMLDivElement>();
  const per = usePeriod(item, s);
  // the now count comes and goes with Google Analytics' realtime report, and takes its room beside the change
  useTick(60_000);
  const now = nowWords(s);
  const behind = useBehind(s, per);
  const d = typeof s.value === "number" ? periodChange(s, per.days, item.props.compare ?? "period") : null;
  const history = item.props.spark !== false && hasHistory(s.points);
  // a source read for a period gives its own days; anything else, its points in the period
  const recent = useMemo(() => (!history ? ([] as Point[]) : s.period ? s.points : inPeriod(s.points, per.days)), [s.points, s.period, history, per.days]);
  const value = typeof s.value === "number" ? formatNumber(s.value, s.format) : String(s.value ?? "—");
  const goodWhen = String(item.props.goodWhen ?? "up");
  const words = behind?.text ?? (d ? null : aside(s, per.days, !!per.source));
  const changed = !!d && !behind;
  const chip = changed ? { delta: deltaText(d!, s.format, isRank(item, s)), label: d!.label } : words ? { delta: "", label: words } : null;
  const st = useMemo(() => dayStats(s, recent, isRank(item, s)), [s, recent, item]);
  const widths = useWidths(value, chip, "rl-mt-v", st, now);
  const box = size.w ? size : { w: item.w - 28, h: item.h - 52 };
  const fit = metricFit(box, value, chip, history && recent.length >= 2, widths.measured, now);
  // the peak and the average sit at the head's far end when the figure, its change and the now count leave room for them
  const m = widths.measured;
  const used = m ? metricHeadW(fit, m) : Infinity;
  const showStats = !!st && !!m && fit.id === "stack" && (used + 1.6) * fit.px + statsWidth(m.stats, fit.px, 0.6) <= box.w;
  // the chart's height, as the fit leaves it: tall enough, it says where its days start and end
  const plotH = fit.id === "stack" ? box.h - (fit.head + METRIC.above) * fit.px + 14 : fit.id === "side" ? box.h : 0;
  // with no room at the head's end, a tall chart gives a line under the head to them
  const statsUnder = !!st && !showStats && fit.id === "stack" && plotH >= 200;
  const dates = plotH >= 70;
  return (
    <div className={cx("mt", `is-${fit.id}`, fit.inline && "is-inline", behind?.reading && "is-reading")} ref={ref} style={fit.px ? { fontSize: quarter(fit.px) } : undefined}>
      {widths.ruler}
      <div className="mt-head">
        <div className="mt-main">
          <b className="mt-v">{value}</b>
          {(chip || now) && (
            <span className="mt-aside">
              {changed ? <Change d={d!} goodWhen={goodWhen} format={s.format} rank={isRank(item, s)} short={fit.short} /> : words ? <span className={cx("mt-label", behind && !behind.reading && "is-warn")}>{words}</span> : null}
              <Live stream={s} />
            </span>
          )}
        </div>
        {showStats && <DayStats st={st!} />}
      </div>
      {statsUnder && <DayStats st={st!} under />}
      {fit.id !== "bare" && (
        <div className={cx("mt-plot", dates && "has-dates")}>
          <TimeChart points={recent} kind="area" range="all" format={s.format} axis={dates ? "dates" : false} scale={Math.min(14, Math.max(11, fit.px * 0.6))} days={!!s.period || wholeDays(recent)} partial={!!s.period && s.period.to >= Math.floor(Date.now() / 86_400_000) * 86_400_000} invert={isRank(item, s)} ghost={item.props.compare !== "none" && s.period ? beforeOf(s) : null} inset={fit.id === "stack" ? 14 : 0} />
        </div>
      )}
    </div>
  );
}

/**
 * How wide the figure, its change and the now count are drawn, in em of the widget's type, measured off a hidden copy
 * set at 100px (layout widths, so the board's zoom does not count), and again whenever that copy changes size: a font
 * that loads late, or a theme with another voice.
 */
function useWidths(value: string, chip: { delta: string; label: string } | null, figure = "rl-mt-v", st: { peak: string; when: string; avg: string } | null = null, now: string | null = null): { measured: (MetricWidths & { stats: number; live: number }) | null; ruler: React.ReactNode } {
  const ref = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState<(MetricWidths & { stats: number; live: number }) | null>(null);
  const full = chip ? [chip.delta, chip.label].filter(Boolean).join(" ") : "";
  const said = st ? `${st.peak} ${st.when} ${st.avg}` : "";
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => {
      const [v, f, sh, stw, lv] = [...el.children].map((c) => (c as HTMLElement).offsetWidth / 100);
      const next = { value: v, full: chip ? f : 0, short: chip ? sh : 0, stats: st ? stw : 0, live: now ? lv : 0 };
      setMeasured((o) => (o && o.value === next.value && o.full === next.full && o.short === next.short && o.stats === next.stats && o.live === next.live ? o : next));
    };
    read();
    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(read);
    for (const c of el.children) ro.observe(c);
    return () => ro.disconnect();
  }, [value, full, chip?.delta, said, now]);
  // as the head draws them: a change with its words, the same alone, or words with no change; the peak and the
  // average; the now count
  const ruler = (
    <div className="mt-ruler" ref={ref} aria-hidden>
      <b className={figure}>{value}</b>
      {chip?.delta ? (
        <>
          <span className="rl-chip">
            <ArrowUpRight strokeWidth={2.4} />
            {chip.delta}
            <span>{chip.label}</span>
          </span>
          <span className="rl-chip">
            <ArrowUpRight strokeWidth={2.4} />
            {chip.delta}
          </span>
        </>
      ) : (
        <>
          <span className="rl-label">{full}</span>
          <span className="rl-label">{full}</span>
        </>
      )}
      {st ? <DayStats st={st} /> : <span />}
      {now ? (
        <span className="rl-lv">
          <i />
          {now}
        </span>
      ) : (
        <span />
      )}
    </div>
  );
  return { measured, ruler };
}

/** A number over its period: the number and its change over a plot that takes the rest of the tile. Search Console's whole performance has its own (google.tsx). */
export function Chart(p: WP) {
  if (p.item.props.look === "performance" && perfOf(p.stream)) return <Performance {...p} />;
  return <Plot {...p} />;
}

function Plot({ item, stream }: WP) {
  const s = stream!;
  const [ref, size] = useSize<HTMLDivElement>();
  const per = usePeriod(item, s);
  const behind = useBehind(s, per);
  const kind = (["area", "line", "bar"].includes(String(item.props.chart)) ? item.props.chart : "area") as "area" | "line" | "bar";
  const d = typeof s.value === "number" ? periodChange(s, per.days, item.props.compare ?? "period") : null;
  useTick(60_000);
  const points = useMemo(() => (s.period ? s.points : inPeriod(s.points, per.days)), [s.points, s.period, per.days]);
  const box = size.w ? size : { w: item.w - 28, h: item.h - 52 };
  // its type as the page sets it, so what fits is measured at the size it is drawn
  const px = quarter(chartType(box));
  const now = nowWords(s);
  const words = behind?.text ?? (d ? null : aside(s, per.days, !!per.source));
  const value = typeof s.value === "number" ? formatNumber(s.value, s.format) : "—";
  const changed = !!d && !behind;
  const st = useMemo(() => dayStats(s, points, isRank(item, s)), [s, points, item]);
  const widths = useWidths(value, changed ? { delta: deltaText(d!, s.format, isRank(item, s)), label: d!.label } : null, "rl-ch-v", st, now);
  const m = widths.measured;
  // the now count, when there is one, shares the line: its width and the gap before it
  const lv = m?.live ? 0.75 + m.live : 0;
  // the change keeps its words while they fit beside the figure; past that, its figure alone, its words in its tooltip
  const short = changed && !!m && (m.value + 0.75 + m.full + lv + 0.2) * px > box.w;
  // the peak and the average at the head's far end, when there is room for them; else, in a tall tile, under it
  const showStats = !!st && !!m && (m.value + 0.75 + (changed ? (short ? m.short : m.full) : 0) + lv + 1.2) * px + statsWidth(m.stats, px, 0.66) <= box.w;
  const statsUnder = !!st && !showStats && item.props.headline !== false && box.h - 4 * px >= 240;
  return (
    <div className={cx("ch", behind?.reading && "is-reading")} ref={ref} style={{ fontSize: px }}>
      {widths.ruler}
      {item.props.headline !== false && (
        <div className="ch-head">
          <b className="ch-v">{value}</b>
          {changed ? <Change d={d!} goodWhen={String(item.props.goodWhen ?? "up")} format={s.format} rank={isRank(item, s)} short={short} /> : words ? <span className={cx("mt-label", behind && !behind.reading && "is-warn")}>{words}</span> : null}
          <Live stream={s} />
          {showStats && <DayStats st={st!} />}
        </div>
      )}
      {statsUnder && <DayStats st={st!} under />}
      <div className="ch-plot">
        {/* a source's own days are drawn one by one, as dates; the board's own points, bucketed as the chart says */}
        {hasHistory(points) || s.period ? (
          <TimeChart points={points} kind={kind} range="all" format={s.format} axis={item.props.axis !== false} agg={item.props.agg === "sum" ? "sum" : "last"} scale={px} days={!!s.period || wholeDays(points)} partial={!!s.period && s.period.to >= Math.floor(Date.now() / 86_400_000) * 86_400_000} invert={isRank(item, s)} ghost={item.props.compare !== "none" && s.period ? beforeOf(s) : null} />
        ) : (
          // one reading is a number, not a line: it says when the line starts rather than draw an empty axis
          <div className="ch-first">
            <svg viewBox="0 0 120 24" preserveAspectRatio="none" aria-hidden>
              <path d="M2 12 H 112" fill="none" />
            </svg>
            <span>{points.length ? `First reading ${timeAgo(points[points.length - 1].t)}. The line starts with the next one.` : "The line starts with its first readings."}</span>
          </div>
        )}
      </div>
    </div>
  );
}

/** "This month", "Last 28 days": the period a goal counts, as its words say it. */
const countsWords = (days: number) => {
  const w = periodSpan(days).replace(/^the /, "");
  return w[0].toUpperCase() + w.slice(1);
};

/**
 * Progress toward a target: a glowing ring with the words under it or beside it, or a bar. A total over a period
 * (orders this month) says its period and the days it has left, and whether it is on pace; a level (MRR) when it
 * gets there at this pace, and the days left to its date when it has one.
 */
export function Goal({ item, stream, readOnly }: WP) {
  const s = stream!;
  const [ref, size] = useSize<HTMLDivElement>();
  useTick(60_000);
  const per = usePeriod(item, s);
  const behind = useBehind(s, per);
  const g = goalView(item.props, s);
  const now = g.value !== null ? formatNumber(g.value, s.format) : "—";
  const of = `of ${formatNumber(g.target, s.format)}`;
  // when: a total's period and the days it has left; a level's days to its date
  const when = [g.total && s.period ? countsWords(s.period.days) : null, g.done ? null : g.left].filter(Boolean).join(" · ") || null;
  // reading another period says so in the pace's place, as a Number does
  const pace = behind ? { text: behind.text, tone: behind.reading ? null : ("warn" as const) } : g.pace;
  const lines = [when, pace?.text ?? null].filter((x): x is string => !!x);
  const fit = goalFit(size.w ? size : { w: item.w - 28, h: item.h - 52 }, item.props.style === "bar" ? "bar" : "ring", now, of, lines);
  // short of the goal never reads as 100%: below it, round down
  const shown = g.done ? Math.round(g.pct * 100) : Math.min(99, Math.floor(Math.max(0, g.pct) * 100));
  const fill = Math.max(0, Math.min(1, g.pct));
  const style = fit.px ? { fontSize: quarter(fit.px) } : undefined;
  // no target yet (a pipeline's "won this quarter" starts without one): the number as it is, and the way to set one
  if (!(g.target > 0))
    return (
      <div className="gl is-bar is-unset" ref={ref} style={style}>
        <div className="gl-top">
          <b className="gl-v">{now}</b>
          {when && <span className="gl-of">{when}</span>}
        </div>
        <div className="gl-track" />
        <div className="gl-foot">
          {readOnly ? (
            <span className="gl-when">No target yet</span>
          ) : (
            <button className="gl-set" onClick={() => set({ selection: [item.id], inspect: true, editing: null })} data-interactive>
              Set a target
            </button>
          )}
        </div>
      </div>
    );
  const Pace = pace ? <span className={cx("gl-pace", pace.tone === "good" && "is-good", pace.tone === "warn" && "is-warn")}>{pace.text}</span> : null;
  const When = when ? <span className="gl-when">{when}</span> : null;
  if (fit.id === "bar")
    return (
      <div className={cx("gl", "is-bar", g.done && "is-done", behind?.reading && "is-reading")} ref={ref} style={style}>
        <div className="gl-top">
          <b className="gl-v">{now}</b>
          <span className="gl-of">{of}</span>
          <span className="gl-pct">{shown}%</span>
        </div>
        <div className="gl-track">
          <i style={{ width: `${fill * 100}%` }} />
          {[0.25, 0.5, 0.75].map((m) => (
            <span key={m} style={{ left: `${m * 100}%` }} />
          ))}
        </div>
        {(When || Pace) && (
          <div className="gl-foot">
            {When}
            {When && Pace && <span className="gl-dot">·</span>}
            {Pace}
          </div>
        )}
      </div>
    );
  const ring = (
    <div className="gl-ring">
      <svg viewBox="0 0 100 100" aria-hidden>
        <circle className="gl-trk" cx="50" cy="50" r="43" />
        <circle className="gl-val" cx="50" cy="50" r="43" pathLength={100} strokeDasharray={`${fill * 100} 100`} transform="rotate(-90 50 50)" />
      </svg>
      <div className="gl-in">
        <b>{shown}%</b>
      </div>
    </div>
  );
  return (
    <div className={cx("gl", `is-${fit.id}`, g.done && "is-done", behind?.reading && "is-reading")} ref={ref} style={style}>
      {ring}
      {fit.id === "side" ? (
        <div className="gl-side">
          <b className="gl-v">{now}</b>
          <span className="gl-of">{of}</span>
          {(When || Pace) && (
            <span className="gl-lines">
              {When}
              {Pace}
            </span>
          )}
        </div>
      ) : (
        <div className="gl-under">
          <span className="gl-of">
            <b>{now}</b> {of}
          </span>
          {When}
          {Pace}
        </div>
      )}
    </div>
  );
}
